/**
 * The one place a withheld proposal field is put into words.
 *
 * The draft, active and archived proposal pages are three separate implementations of the same seven
 * tabs, and each used to phrase its own refusal. The same withheld fee schedule read "…the fee
 * schedule for this proposal." on a draft and "…the fee schedule." on an active one; a withheld
 * discount read "…view the discount for this proposal.", "…view *or edit* the discount." and — on the
 * archived page — as `0.00%`, because that tab had no refusal at all and fell back to a default.
 *
 * That last one is the reason this module exists. Divergent wording is a cosmetic problem; it was the
 * symptom of each page deciding independently whether to refuse, which is where a page comes to
 * answer a question it should have declined. Routing every refusal through one map does not by itself
 * stop a tab from forgetting to ask — but it removes the per-page copy that made the drift invisible,
 * and gives a reviewer one list to check a new tab against.
 *
 * The distinctions that remain are real ones, not page differences: a team, the teams collection, and
 * the members within a team are three different refusals because they are three different questions.
 */
export const MaskedProposalField = {
  Conflicts: "conflicts",
  Discount: "discount",
  FeeSchedule: "feeSchedule",
  Policies: "policies",
  /** One named team the viewer may not read. */
  Team: "team",
  /** The teams collection as a whole. */
  Teams: "teams",
  /** The membership of a team the viewer may otherwise see. */
  TeamMembers: "teamMembers",
} as const;

export type MaskedProposalFieldKey = (typeof MaskedProposalField)[keyof typeof MaskedProposalField];

const notices: Record<MaskedProposalFieldKey, string> = {
  conflicts: "You do not have permission to view contracts.",
  discount: "You do not have permission to view the discount.",
  feeSchedule: "You do not have permission to view the fee schedule.",
  policies: "You do not have permission to view policy documents.",
  team: "You do not have permission to view this team.",
  teams: "You do not have permission to view teams.",
  teamMembers: "You do not have permission to view team members.",
};

/**
 * What to show instead of a field the viewer may not read.
 *
 * Always a refusal, never a placeholder value: a masked field must not be reported as empty, zero, or
 * any other plausible reading of the record. On an archived proposal in particular the terms are the
 * ones that were hired, so a substituted default is a false statement about the agreement rather than
 * a hidden one.
 */
export function maskedProposalFieldNotice(field: MaskedProposalFieldKey): string {
  return notices[field];
}
